import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { api } from '../services/api.service';
import { toast } from 'sonner';
import { shouldSkipApiCalls } from '../utils/routeUtils';

interface TimezoneContextType {
  timezone: string;
  setTimezone: (timezone: string) => void;
  updateTimezone: (timezone: string) => Promise<void>;
  loading: boolean;
}

const TimezoneContext = createContext<TimezoneContextType | undefined>(undefined);

export const useTimezone = () => {
  const context = useContext(TimezoneContext);
  if (!context) {
    throw new Error('useTimezone must be used within a TimezoneProvider');
  }
  return context;
};

interface TimezoneProviderProps {
  children: ReactNode;
}

export const TimezoneProvider: React.FC<TimezoneProviderProps> = ({ children }) => {
  const [timezone, setTimezone] = useState<string>('UTC');
  const [loading, setLoading] = useState(true);

  // Fetch timezone setting on mount (skip on public pages)
  useEffect(() => {
    // Skip timezone fetch on public pages to avoid unnecessary API calls
    if (shouldSkipApiCalls()) {
      // Use cached timezone or default for public pages
      const savedTimezone = localStorage.getItem('app_timezone');
      if (savedTimezone) {
        setTimezone(savedTimezone);
      }
      setLoading(false);
      return;
    }

    const fetchTimezone = async () => {
      try {
        const response = await api.settings.getTimezone();
        if (response && response.timezone) {
          setTimezone(response.timezone);
          // Store in localStorage for immediate access
          localStorage.setItem('app_timezone', response.timezone);
        }
      } catch (error) {
        console.error('Failed to fetch timezone:', error);
        // Fallback to localStorage or UTC
        const savedTimezone = localStorage.getItem('app_timezone');
        if (savedTimezone) {
          setTimezone(savedTimezone);
        }
      } finally {
        setLoading(false);
      }
    };

    fetchTimezone();
  }, []);

  const updateTimezone = async (newTimezone: string) => {
    setLoading(true);
    try {
      await api.settings.updateTimezone({ timezone: newTimezone });
      setTimezone(newTimezone);
      localStorage.setItem('app_timezone', newTimezone);
      toast.success('Timezone updated successfully');
    } catch (error) {
      console.error('Failed to update timezone:', error);
      toast.error('Failed to update timezone');
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const value = {
    timezone,
    setTimezone,
    updateTimezone,
    loading
  };

  return (
    <TimezoneContext.Provider value={value}>
      {children}
    </TimezoneContext.Provider>
  );
};